import {
  RoutineQuestionConfig,
  RoutineType,
  useWorkspaceRoutine,
  WorkflowPageConfig,
  WorkflowQuestionConfig,
  WorkflowQuestionType,
  WorkflowRoutineConfig,
} from '@gripp/shared-logic';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { RuledLines } from '../components/printPrimitives';
import { PrintQuestionBlock } from '../components/printQuestionBlock';
import { PRINT_SHEET_CLASS as C } from '../printStyles';
import { buildSingleStateQuestion } from '../singleStateQuestion';
import { BarcodePrintQuestion } from './barcodeScanner';
import { CheckboxPrintQuestion } from './checkbox';
import { CounterPrintQuestion } from './counter';
import { LabelPrintQuestion } from './label';
import { LocationPrintQuestion } from './location';
import { MediaPrintQuestion } from './media';
import { QuickConfirmPrintQuestion } from './quickConfirm';
import { RadioPrintQuestion } from './radio';
import { SignaturePrintQuestion } from './signature';
import { StatusPrintQuestion } from './status';
import { TextPrintQuestion } from './text';
import { TimePrintQuestion } from './time';
import {
  MAX_NESTED_PRINT_DEPTH,
  RoutinePrintQuestion,
  RoutinePrintQuestionProps,
} from './types';

/**
 * A question pointing at another routine. The referenced routine is expanded
 * inline so the writer sees the fields rather than a cross-reference, but only
 * one level deep: past that the nested routine's title is printed on its own,
 * which keeps a routine that points back at its own parent from producing an
 * unbounded form.
 */
const NestedRoutinePrintQuestion: RoutinePrintQuestion = ({ config, depth }) => {
  const nested = config as RoutineQuestionConfig;
  const routineId = nested.workspaceRoutineId ?? '';
  const routine = useWorkspaceRoutine(routineId);

  /**
   * Whether to print a writable line is decided by the config alone, never by
   * whether the lookup has come back. A reference that was going to resolve
   * must never print as a plain line: that is a form that looks complete and
   * is missing a field, which the person holding the clipboard cannot detect.
   * Template routines are routinely saved with the reference left unbound, and
   * that question still has to be answerable, so an unbound one -- which never
   * fetches, so it is settled the moment it renders -- gets the line.
   */
  if (!routineId) return <RuledLines />;
  if (!routine) return null;

  const title = routine.config?.title || routine.name || '';

  if (depth >= MAX_NESTED_PRINT_DEPTH) {
    return <div className={`${C}__nestedTitle`}>{title}</div>;
  }

  return (
    <div>
      <div className={`${C}__nestedTitle`}>{title}</div>
      <RoutinePrintBody routine={routine} depth={depth + 1} />
    </div>
  );
};

export const RoutinePrintQuestionModules: Record<
  WorkflowQuestionType,
  RoutinePrintQuestion
> = {
  [WorkflowQuestionType.BarcodeScanner]: BarcodePrintQuestion,
  [WorkflowQuestionType.Checkbox]: CheckboxPrintQuestion,
  [WorkflowQuestionType.Counter]: CounterPrintQuestion,
  [WorkflowQuestionType.Label]: LabelPrintQuestion,
  [WorkflowQuestionType.Location]: LocationPrintQuestion,
  [WorkflowQuestionType.Media]: MediaPrintQuestion,
  [WorkflowQuestionType.QuickConfirm]: QuickConfirmPrintQuestion,
  [WorkflowQuestionType.Radio]: RadioPrintQuestion,
  [WorkflowQuestionType.Routine]: NestedRoutinePrintQuestion,
  [WorkflowQuestionType.Signature]: SignaturePrintQuestion,
  [WorkflowQuestionType.Status]: StatusPrintQuestion,
  [WorkflowQuestionType.Text]: TextPrintQuestion,
  [WorkflowQuestionType.Time]: TimePrintQuestion,
};

/** Renders only the answer area for one question, with no heading. */
export const PrintQuestionAnswer: FC<RoutinePrintQuestionProps> = ({ config, depth }) => {
  const Renderer = RoutinePrintQuestionModules[config.type];
  if (!Renderer) return null;
  return <Renderer config={config} depth={depth} />;
};

const byOrder = <T extends { order: number }>(a: T, b: T): number =>
  (a.order ?? 0) - (b.order ?? 0);

type PrintQuestionListProps = {
  questions: WorkflowQuestionConfig[];
  depth: number;
  /** Omitted for nested routines, whose fields are not numbered separately. */
  startNumber?: number;
};

export const PrintQuestionList: FC<PrintQuestionListProps> = ({
  questions,
  depth,
  startNumber,
}) => {
  const { t } = useTranslation();
  const requiredLabel = t('routine.print.required', 'required');

  return (
    <>
      {[...questions].sort(byOrder).map((question, index) => (
        <PrintQuestionBlock
          key={question.id ?? index}
          config={question}
          number={startNumber === undefined ? undefined : startNumber + index}
          requiredLabel={requiredLabel}
        >
          <PrintQuestionAnswer config={question} depth={depth} />
        </PrintQuestionBlock>
      ))}
    </>
  );
};

type RoutinePrintBodyProps = {
  routine: { type?: RoutineType; name?: string; id: string; config: WorkflowRoutineConfig | object };
  depth: number;
};

/**
 * The answerable part of any routine: workflow pages for a Workflow, a single
 * affordance for the other three types.
 */
export const RoutinePrintBody: FC<RoutinePrintBodyProps> = ({ routine, depth }) => {
  if (routine.type === RoutineType.Workflow) {
    const config = routine.config as WorkflowRoutineConfig;
    const pages = [...(config?.pages ?? [])].sort(byOrder);
    // A single page carries the routine's own name, so its heading would just
    // repeat the title block; multi-page routines label each section.
    const showPageTitles = pages.length > 1;
    let number = 1;

    return (
      <>
        {pages.map((page: WorkflowPageConfig, index) => {
          const questions = page.questions ?? [];
          // Only the root routine's own questions are numbered; a nested
          // routine's fields sit inside a numbered parent question and
          // restarting the count there would print duplicate numbers.
          const startNumber = depth === 0 ? number : undefined;
          number += questions.length;

          return (
            <section key={page.id ?? index} className={`${C}__section`}>
              {showPageTitles && page.name ? (
                <h2 className={`${C}__sectionTitle`}>{page.name}</h2>
              ) : null}
              {page.instructions?.trim() ? (
                <p className={`${C}__sectionInstructions`}>{page.instructions}</p>
              ) : null}
              <PrintQuestionList
                questions={questions}
                depth={depth}
                startNumber={startNumber}
              />
            </section>
          );
        })}
      </>
    );
  }

  const singleStateQuestion = buildSingleStateQuestion(
    routine as Parameters<typeof buildSingleStateQuestion>[0]
  );
  if (!singleStateQuestion) return null;

  // The routine's instructions already sit in the sheet header, so the single
  // affordance prints without repeating them as a question heading. Expanded
  // inside a nested question it takes no block spacing of its own -- the
  // question it is expanded into already carries that.
  return depth === 0 ? (
    <div className={`${C}__question`}>
      <PrintQuestionAnswer config={singleStateQuestion} depth={depth} />
    </div>
  ) : (
    <PrintQuestionAnswer config={singleStateQuestion} depth={depth} />
  );
};
